import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import Router from 'next/router';
import NextHead from 'next/head';
import componentLibraryManifest from '@deliveroo/consumer-component-library/manifest';
import ttiPolyfill from 'tti-polyfill';

import Head from '../../components/Head';

import { isFeatureActive } from '../../selectors/configSelectors';
import { ONETRUST_COOKIE_CONSENT } from '../../flags/features';

import speedTrackingCreator from '../../utils/speedTracking';
import styles from './styles.scss';

import safariPinnedTab from './images/favicons/safari-pinned-tab.svg';

import developmentFavicon from './images/favicons/development/favicon-32x32.png';
import developmentAppleTouchIcon from './images/favicons/development/apple-touch-icon.png';

import stagingFavicon from './images/favicons/staging/favicon-32x32.png';
import stagingAppleTouchIcon from './images/favicons/staging/apple-touch-icon.png';

import productionFavicon from './images/favicons/production/favicon-32x32.png';
import productionAppleTouchIcon from './images/favicons/production/apple-touch-icon.png';

const TOUCH_ICON_SIZES = [57, 72, 120, 144, 152];

let favicon;
let appleTouchIcon;
let themeColor;
switch (process.env.ENVIRONMENT_NAME) {
  case 'production':
    favicon = productionFavicon;
    appleTouchIcon = productionAppleTouchIcon;
    themeColor = '#009e99';
    break;
  case 'staging':
    favicon = stagingFavicon;
    appleTouchIcon = stagingAppleTouchIcon;
    themeColor = '#e25d50';
    break;
  default:
    favicon = developmentFavicon;
    appleTouchIcon = developmentAppleTouchIcon;
    themeColor = '#7a7a7a';
}

// These fonts are the ones that get downloaded when loading the homepage and the
// restaurant list. Try to keep this list to a minimum
const fontsToPreload = [
  'static/fonts/IBMPlexSans-Regular.woff2',
  'static/fonts/stratos-semibold.woff2',
  'static/fonts/IBMPlexSans-Bold.woff2',
];

export class Page extends Component {
  componentDidMount() {
    const { pageName, customTrackingTags, trackTTI } = this.props;
    let rooTTI;
    // calculating time to interactive
    ttiPolyfill.getFirstConsistentlyInteractive().then((tti) => {
      if (tti) {
        rooTTI = tti;
        // Send TTI to segment for analytics if required.
        if (trackTTI) {
          trackTTI(tti);
        }
      }
    });

    const getTTI = () => rooTTI;

    if (typeof window !== 'undefined') {
      window.addEventListener('unload', speedTrackingCreator(pageName, customTrackingTags, getTTI));
    }
    Router.onRouteChangeStart = speedTrackingCreator(pageName, customTrackingTags, getTTI);
  }

  render() {
    const {
      children,
      color,
      renderWrapper,
      pageTitle,
      ogTitle,
      metaDescription,
      metadata,
      ogDescription,
      image,
      pagePath,
      alternateLocales,
      currentCountry,
      currentLocale,
      currentUrl,
      robots,
      isOneTrustCookieConsentEnabled,
    } = this.props;

    const { ENVIRONMENT_NAME: environment } = process.env;

    return (
      <React.Fragment>
        <Head
          pageTitle={pageTitle}
          ogTitle={ogTitle}
          metaDescription={metaDescription}
          ogDescription={ogDescription}
          image={image}
          pagePath={pagePath || currentUrl}
          alternateLocales={alternateLocales}
          currentCountry={currentCountry}
          currentLocale={currentLocale}
          robots={robots}
          metadata={metadata}
          isOneTrustCookieConsentEnabled={isOneTrustCookieConsentEnabled}
        />
        <NextHead>
          <link color={themeColor} href={safariPinnedTab} rel="mask-icon" />
          {TOUCH_ICON_SIZES.map((size) => (
            <link
              key={size}
              rel="apple-touch-icon-precomposed"
              href={appleTouchIcon}
              sizes={`${size}x${size}`}
            />
          ))}
          <link rel="shortcut icon" href={favicon} type="image/png" sizes="32x32" />

          <link
            rel="stylesheet"
            type="text/css"
            href={componentLibraryManifest['consumer-component-library.css']}
          />

          {fontsToPreload.map((cclFontFilename) => (
            <link
              key={cclFontFilename}
              rel="preload"
              href={componentLibraryManifest[cclFontFilename]}
              as="font"
              type="font/woff2"
              crossOrigin="anonymous"
            />
          ))}

          {environment !== 'production' && <meta name="robots" content="noindex" />}
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <meta httpEquiv="X-UA-Compatible" content="IE=edge,chrome=1" />
          <meta name="theme-color" content={themeColor} />
        </NextHead>
        {renderWrapper(
          <div
            className={classnames(styles.page, {
              [styles[color]]: Boolean(color),
            })}
          >
            {children}
          </div>,
        )}
        {/* eslint-disable react/no-danger */}
        <script
          dangerouslySetInnerHTML={{
            __html: `
            if(navigator.serviceWorker) {
              navigator.serviceWorker.getRegistrations().then((registrations) => {
                registrations.forEach((reg) => reg.unregister());
              });
              if(window.caches) {
                caches.keys().then((cacheNames) => {
                  cacheNames.forEach(name => caches.delete(name))
                });
              }
            }
          `,
          }}
        />
        {/* eslint-enable react/no-danger */}
      </React.Fragment>
    );
  }
}

Page.propTypes = {
  ...Head.propTypes,
  children: PropTypes.node.isRequired,
  color: PropTypes.oneOf(['anchovy']),
  pageName: PropTypes.string,
  renderWrapper: PropTypes.func,
  customTrackingTags: PropTypes.arrayOf(PropTypes.string),
  trackTTI: PropTypes.func,
  pagePath: PropTypes.string,
  /**
   * To support arbitrary metadata tags, this component can also
   * accept an array of raw <meta> tags here.
   */
  metadata: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      property: PropTypes.string,
      content: PropTypes.string.isRequired,
    }),
  ),
};

Page.defaultProps = {
  ...Head.defaultProps,
  color: undefined,
  pageName: null,
  renderWrapper: (children) => <div>{children}</div>,
  customTrackingTags: [],
  trackTTI: undefined,
  pagePath: undefined,
  metadata: [],
};

const mapStateToProps = (state) => ({
  currentCountry: state.request.country,
  currentLocale: state.request.locale,
  // if pagePath prop isn't provided, fallback to host + currentUrl
  currentUrl: `${state.request.host}${state.request.currentUrl}`,
  isOneTrustCookieConsentEnabled: isFeatureActive(state, ONETRUST_COOKIE_CONSENT),
});

export default connect(mapStateToProps)(Page);
